import React, { useState } from 'react';
import { Box, Button, CircularProgress } from '@mui/material';
import BasicModalConfirmation from '../../../../../../../../../components/basicModalConfirmation';

interface AutobuyActionsProps {
  onToggleAutoBuys: () => void;
  onSave: () => void;
  isLoading: boolean;
  isSaving: boolean;
  hasErrors?: boolean;
  isAutoBuyActive: boolean;
}

/**
 * Component for rendering the actions row in AutobuyContent
 */
export default function AutobuyActions({
  onToggleAutoBuys,
  onSave,
  isLoading,
  isSaving,
  hasErrors = false,
  isAutoBuyActive
}: AutobuyActionsProps) {
  // State for tracking if the confirmation modal is open
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Functions to handle modal visibility
  const openModal = () => {
    // If we're stopping AutoBuy (isAutoBuyActive is true), execute directly
    if (isAutoBuyActive) {
      onToggleAutoBuys();
    } else {
      // Only show confirmation modal when starting AutoBuy
      setIsModalOpen(true);
    }
  };
  
  const closeModal = () => setIsModalOpen(false);

  // Function that will be called when user confirms in the modal
  const handleConfirmToggle = () => {
    onToggleAutoBuys();
    closeModal();
  };

  // Generate the appropriate modal title based on current state
  const modalTitle = isAutoBuyActive 
    ? 'Are you sure you want to stop Auto Buys? This action will pause all automatic approvals of Buy Requests until Auto Buy is re-enabled.'
    : 'Are you sure you want to start Auto Buys? This will automatically approve Buy Requests based on the current Auto Buy rules and settings.';

  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 3 }}>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          color={isAutoBuyActive ? "error" : "success"}
          onClick={openModal}
          disabled={isLoading || isSaving || hasErrors}
        >
          {isAutoBuyActive ? 'Stop AUTO BUYS' : 'Save and Start AUTO BUYS'}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={onSave}
          disabled={isLoading || isSaving || hasErrors}
        >
          {isSaving ? <CircularProgress size={24} color="inherit" /> : 'Save Changes'}
        </Button>

        {/* Confirmation Modal */}
        <BasicModalConfirmation
          open={isModalOpen}
          handleClose={closeModal}
          onClick={handleConfirmToggle}
          title={modalTitle}
          confirmLabel="Continue"
          cancelLabel="Cancel"
        />
      </Box>
    </Box>
  );
}
